import { useRouter } from 'next/compat/router';
import Link from 'next/link';
import styled from 'styled-components';
import {
  captureEventTrigger,
  EventType,
  getPageName,
  logAnalyticsEvent,
  PageTags,
} from '@pass-frontend/common/analytics';
import { ContentWidthWrapper } from '@pass-frontend/common/components/content-width-wrapper/ui';
import {
  getLoginURL,
  STANDALONE_PATHS,
} from '@pass-frontend/common/constants/paths';
import { useIntl } from '@pass-frontend/common/i18n';
import { useHSIDLoginCheck } from '@pass-frontend/common/login-data-bridge';
import { MEDIA_QUERY } from '@pass-frontend/utility/style-helpers';

interface HeaderProps {
  isCommercial?: boolean;
  hideGetMemberCodeLink?: boolean;
}

interface StyledLogoProps {
  isCommercial?: boolean;
}

const StyledHeader = styled.div`
  border-bottom: 1px solid #e0e0e0;
  box-sizing: border-box;
  padding: 16px 0;

  .btn {
    text-decoration: none;
  }

  ${MEDIA_QUERY.desktop} {
    padding: 24px 0;
  }

  .get-member-button {
    margin-left: auto;
    outline: none;
    margin-right: 24px;

    ${MEDIA_QUERY.mobile} {
      font-size: 10px;
    }
  }
`;

const StyledLogo = styled.a<StyledLogoProps>`
  width: ${(props) => (props.isCommercial ? '137px' : '100px')};

  ${MEDIA_QUERY.desktop} {
    width: ${(props) => (props.isCommercial ? '273px' : '173px')};
  }
`;

export function Header({ isCommercial, hideGetMemberCodeLink }: HeaderProps) {
  const { t } = useIntl();
  const router = useRouter();
  const { isUserAuthenticated: isHSIDAuthenticated } = useHSIDLoginCheck();

  const handleLogoClick = (e) => {
    captureEventTrigger(e);
  };

  function handleClick(event: any) {
    logAnalyticsEvent(
      EventType.PageNav,
      {
        pageName: getPageName(router?.pathname ?? '/'),
        pageTags: { [PageTags.NewPageLoad]: false },
      },
      event.nativeEvent,
    );
  }

  return (
    <>
      {!isHSIDAuthenticated && (
        <StyledHeader className="has-bg-warm-white">
          <ContentWidthWrapper>
            <div className="is-display-flex is-justify-content-space-between is-align-items-center">
              <Link href="/" legacyBehavior>
                <StyledLogo
                  isCommercial={isCommercial}
                  onClick={handleLogoClick}
                >
                  <img
                    src="images/logo.svg"
                    alt={t('header.logo.altText')}
                    data-ui-element-name="one-pass-logo"
                  />
                </StyledLogo>
              </Link>
              {!isCommercial && !hideGetMemberCodeLink && (
                <a
                  className="is-body-1-bold get-member-button"
                  href={STANDALONE_PATHS.MEMBER_CODE_CHECK}
                  data-ui-element-name="member-code"
                  onClick={handleClick}
                >
                  {t('header.getMemberCode')}
                </a>
              )}
              <a
                className="btn btn-secondary"
                href={getLoginURL(router?.locale)}
                data-ui-element-name="log-in"
                onClick={handleClick}
              >
                {t('header.loginText')}
              </a>
            </div>
          </ContentWidthWrapper>
        </StyledHeader>
      )}
    </>
  );
}

export default Header;
